import { Injectable, Optional } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';
import { AuditTransactionService } from '../audit/audit-transaction.service';

/** The single stock-keeping location every store gets until it needs more. */
export const DEFAULT_LOCATION_CODE = 'default';
export const DEFAULT_LOCATION_NAME = 'Default location';

/**
 * The stock ledger.
 *
 * **Why this service exists.** The schema has carried a proper inventory model
 * since early on — `InventoryItem` (one per product, or per *variant*),
 * `InventoryLevel` (`available`/`committed`, per location) and an
 * `InventoryMovement` audit trail — and `checkout.service.ts` already reserves
 * and commits against it. What was missing was anything that *created those
 * rows*. With none of them present, every checkout fell through
 * `if (!inventoryItem?.tracked) continue` and no purchase ever moved stock.
 *
 * So this is the provisioning and reconciliation half that was never written,
 * not a new model.
 *
 * ## The two numbers
 *
 * `available` is what the store physically holds. `committed` is the part of it
 * already spoken for by a checkout in progress. What a shopper can still buy is
 * the difference:
 *
 * ```
 *   sellable = available - committed
 * ```
 *
 * A reservation raises `committed`; completing the order lowers both, which is
 * the moment the goods actually leave. Cancelling lowers `committed` alone.
 *
 * ## Why `products.Inventory` still exists
 *
 * `Inventory.quantity` is a *derived cache* of the sellable total, kept because
 * `Product.inventory` is a to-one relation that the `inStock` filter, the
 * out-of-stock and low-stock status filters, the dashboard counts and every
 * product payload read directly. Turning that into a per-variant join would
 * change the shape of all of them. So the ledger is the fact and the cache is
 * what everything else keeps reading.
 */
@Injectable()
export class InventoryLedgerService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService,
    /**
     * Optional like the collaborators above — the specs build this service
     * bare. Without it the change rows are still written by the trigger, they
     * just carry `system` as the actor instead of the signed-in user.
     */
    @Optional()
    private readonly auditTx?: AuditTransactionService
  ) {}

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }

  /**
   * The store's default location, created on first use.
   *
   * Multi-location is a real feature of the schema and deliberately not a
   * feature of this service yet: nothing in the product asks a merchant which
   * warehouse a sale came out of. One location per store keeps the ledger
   * honest without inventing a concept the UI cannot express.
   */
  async ensureLocation(tx: Prisma.TransactionClient = this.prisma): Promise<string> {
    const storeId = this.storeId();

    const existing = await tx.inventoryLocation.findFirst({
      where: { storeId, code: DEFAULT_LOCATION_CODE },
      select: { id: true },
    });
    if (existing) return existing.id;

    const created = await tx.inventoryLocation.create({
      data: {
        storeId,
        code: DEFAULT_LOCATION_CODE,
        name: DEFAULT_LOCATION_NAME,
      },
      select: { id: true },
    });
    return created.id;
  }

  /**
   * The ledger row for one sellable thing, created if absent.
   *
   * `variantId` null means the product itself — which is the right key for a
   * product sold in exactly one form. A product with variants has one item per
   * variant and none for the bare product, because there is nothing to buy at
   * the product level.
   */
  async ensureItem(
    productId: string,
    variantId: string | null,
    options: { sku?: string | null; initialAvailable?: number } = {},
    tx: Prisma.TransactionClient = this.prisma
  ): Promise<string> {
    const storeId = this.storeId();

    const existing = await tx.inventoryItem.findFirst({
      where: { storeId, productId, variantId, isActive: true },
      select: { id: true },
    });

    const itemId = existing
      ? existing.id
      : (
          await tx.inventoryItem.create({
            data: { storeId, productId, variantId, sku: options.sku ?? null, tracked: true },
            select: { id: true },
          })
        ).id;

    const locationId = await this.ensureLocation(tx);
    const level = await tx.inventoryLevel.findFirst({
      where: { inventoryItemId: itemId, locationId },
      select: { id: true },
    });

    if (!level) {
      await tx.inventoryLevel.create({
        data: {
          storeId,
          inventoryItemId: itemId,
          locationId,
          available: Math.max(0, options.initialAvailable ?? 0),
          committed: 0,
        },
      });
    }

    return itemId;
  }

  /** Stop tracking a line — used when a variant is soft-deleted. */
  async deactivateItem(
    productId: string,
    variantId: string,
    tx: Prisma.TransactionClient = this.prisma
  ): Promise<void> {
    await tx.inventoryItem.updateMany({
      where: { storeId: this.storeId(), productId, variantId },
      data: { isActive: false },
    });
  }

  /**
   * Set what the store holds, as a merchant typing a number means it.
   *
   * They are stating on-hand stock, not sellable stock, so `committed` is left
   * alone — a checkout already in flight keeps its claim. Recorded as a movement
   * so a later "where did the stock go" question has an answer.
   */
  async setAvailable(
    productId: string,
    variantId: string | null,
    quantity: number,
    reason = 'Merchant stock update'
  ): Promise<void> {
    const storeId = this.storeId();
    const next = Math.max(0, Math.trunc(quantity));

    await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const itemId = await this.ensureItem(productId, variantId, { initialAvailable: next }, tx);
      const locationId = await this.ensureLocation(tx);

      const level = await tx.inventoryLevel.findFirst({
        where: { inventoryItemId: itemId, locationId },
        select: { id: true, available: true },
      });
      if (!level) return;

      const delta = next - level.available;
      if (delta === 0) return;

      await tx.inventoryLevel.update({
        where: { id: level.id },
        data: { available: next },
      });
      await tx.inventoryMovement.create({
        data: {
          storeId,
          inventoryItemId: itemId,
          locationId,
          changeType: 'adjust',
          quantityDelta: delta,
          reason,
          referenceType: 'product',
          referenceId: productId,
        },
      });
    });

    await this.syncProductCache(productId);
  }

  /**
   * What a shopper can still buy of one specific thing.
   *
   * `null` means the ledger has no opinion — either no row was provisioned for
   * this line, or the merchant has marked it untracked. Callers fall back to
   * the cached product total rather than treating that as zero: refusing to
   * sell something because provisioning missed it is a worse failure than
   * selling it against a slightly stale number.
   */
  async sellable(productId: string, variantId: string | null): Promise<number | null> {
    const item = await this.prisma.inventoryItem.findFirst({
      where: { storeId: this.storeId(), productId, variantId, isActive: true },
      select: { id: true, tracked: true },
    });

    if (!item || !item.tracked) return null;

    const levels = await this.prisma.inventoryLevel.findMany({
      where: { inventoryItemId: item.id, isActive: true },
      select: { available: true, committed: true },
    });

    return levels.reduce((sum, l) => sum + Math.max(0, l.available - l.committed), 0);
  }

  /**
   * Sellable totals for many products at once, keyed by `productId:variantId`.
   * A missing key carries the same meaning as a null from `sellable()`.
   */
  async sellableMap(
    lines: { productId: string; variantId?: string | null }[]
  ): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    if (!lines.length) return result;

    const items = await this.prisma.inventoryItem.findMany({
      where: {
        storeId: this.storeId(),
        productId: { in: [...new Set(lines.map((l) => l.productId))] },
        isActive: true,
      },
      select: { id: true, productId: true, variantId: true, tracked: true },
    });

    const levels = await this.prisma.inventoryLevel.findMany({
      where: { inventoryItemId: { in: items.map((i) => i.id) }, isActive: true },
      select: { inventoryItemId: true, available: true, committed: true },
    });

    const byItem = new Map<string, number>();
    for (const level of levels) {
      const current = byItem.get(level.inventoryItemId) ?? 0;
      byItem.set(level.inventoryItemId, current + Math.max(0, level.available - level.committed));
    }

    for (const item of items) {
      if (!item.tracked) continue;
      result.set(`${item.productId}:${item.variantId ?? ''}`, byItem.get(item.id) ?? 0);
    }

    return result;
  }

  /**
   * Recompute `products.Inventory.quantity` from the ledger.
   *
   * The product total is a **sum** across its variants, not a mirror of one of
   * them the way price is: "is this in stock?" means "is any size in stock?".
   * A product whose 500 ml is gone but whose 1 L is not is still purchasable,
   * and the category page should keep saying so.
   */
  async syncProductCache(productId: string): Promise<void> {
    const storeId = this.storeId();

    const items = await this.prisma.inventoryItem.findMany({
      where: { storeId, productId, isActive: true, tracked: true },
      select: { id: true },
    });

    const levels = items.length
      ? await this.prisma.inventoryLevel.findMany({
          where: { inventoryItemId: { in: items.map((i) => i.id) }, isActive: true },
          select: { available: true, committed: true },
        })
      : [];

    const total = levels.reduce((sum, l) => sum + Math.max(0, l.available - l.committed), 0);

    await this.prisma.inventory.upsert({
      where: { productId },
      create: { storeId, productId, quantity: total },
      update: { quantity: total },
    });
  }
}
